import moment from 'moment';
import { FeatureEvent } from 'src/app/modules/cop/drawing/features/feature.model';
import { GeoJsonUtilityService } from 'src/app/modules/cop/drawing/geo-json-utility.service';
import { LayerColorsService } from 'src/app/modules/cop/drawing/layer-colors.service';
import { Layer } from 'src/app/modules/cop/store/cop/models/layer.model';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { Document } from 'src/app/modules/core/store/models/document.model';
import { LayerColors } from '../../models/layer-colors.model';
import { GoogleMapFeatures } from '../../models/layer-map-features.model';
import { MapDrawingContext } from '../../models/map-drawing-context.model';
import { Feature, FeatureRenderOptions, MarkerUrls, SELECTED_Z_INDEX } from '../feature.model';
import { v4 as uuidv4 } from 'uuid';
import { GeometryTypes } from '../../models/layer-renderer.model';

export class AlertFlightFeature implements Feature {
  private googleMapFeatures: GoogleMapFeatures;
  private zIndex: number = 0;
  private isVisible: boolean = false;
  private isSelected: boolean = false;
  private layerColors: LayerColors;
  private mouseoutTimers = {};
  private layerUId: string;
  private datasetUId: string;
  private documentUId: string;
  private markerUrls: { normal: string; selected: string } = { normal: '', selected: '' };
  private document: Document;
  private googleMap: google.maps.Map;
  private timerId: any;

  render(context: MapDrawingContext, options: FeatureRenderOptions): void {
    const { layer, document } = options;

    this.googleMapFeatures = context.mapFeatures[layer.uId][document.uid];

    const dataset = context.mapState.datasetsSet[layer.datasetUId];

    this.layerUId = layer.uId;
    this.datasetUId = dataset.uId;
    this.documentUId = document.uid;
    this.isSelected = !!context.mapState.selectedDocuments[document.uid];
    this.isVisible = context.isDocumentVisible(layer, document);
    this.document = document;
    this.googleMap = context.googleMap;

    const fillColor = LayerColorsService.getLayerFillColor(document.fill_color ?? layer.color);
    const borderColor = LayerColorsService.getLayerBorderColorFromFillColor(fillColor);
    this.layerColors = {
      fillColor,
      borderColor
    };

    this.markerUrls = this.buildMarkerImageUrls(layer, dataset, document, context.cacheBuster);

    const geometries = this.getGeometryFeaturesForDisplay(document);
    var [lng, lat] = [0, 0];

    if (geometries?.length > 0) {
      if (geometries[0].type === GeometryTypes.feature) {
        [lng, lat] = geometries[0].geometry?.type === GeometryTypes.point ? geometries[0].geometry.coordinates : [0, 0];
      } else if (geometries[0].type === GeometryTypes.point) {
        [lng, lat] = geometries[0].coordinates || [0, 0];
      }
    }

    if (this.googleMapFeatures.markers.length > 0) {
      this.updateFlight(context, document, lat, lng);
    } else {
      this.addFlight(context, layer, dataset, document, lat, lng);
    }

    if (this.isSelected) {
      this.drawPlaneTrack(document, context.googleMap);
    }
  }

  addFlight(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, lat: number, lng: number): void {
    const anchor = new google.maps.Point(16, 18);

    const marker = new google.maps.Marker({
      position: { lat, lng },
      icon: {
        url: !this.isSelected ? this.markerUrls.normal : this.markerUrls.selected,
        anchor
      },
      visible: this.isVisible,
      zIndex: this.zIndex ?? 3000,
      map: context.googleMap
    });

    marker['__data'] = {
      id: uuidv4(),
      layerUId: layer.uId,
      datasetUId: dataset.uId,
      documentUId: document.uid
    };

    this.googleMapFeatures.markers.push(marker);

    const mapEventService = context.mapEvents;

    marker.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });
    });

    marker.addListener('mouseover', () => {
      this.onMarkerMouseOver({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });
    });

    marker.addListener('mouseout', () => {
      this.onMarkerMouseOut({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });
    });
  }

  updateFlight(context: MapDrawingContext, document: Document, lat: number, lng: number): void {
    const marker = this.googleMapFeatures.markers[0];

    const anchor = new google.maps.Point(16, 18);

    marker.setPosition({
      lat,
      lng
    });

    marker.setIcon({
      url: this.isSelected ? this.markerUrls.selected : this.markerUrls.normal,
      anchor
    });

    this.isSelected ? this.drawPlaneTrack(document, context.googleMap) : this.hidePlaneTrack();
  }

  drawPlaneTrack(document: Document, googleMap: google.maps.Map) {
    this.hidePlaneTrack();

    let positions;
    try {
      positions = JSON.parse(document.flight_positions) as any[];
    } catch (error) {
      console.error(error);
      return;
    }

    positions.sort((p1, p2) => {
      const mp1 = moment(p1.lastUpdate);
      const mp2 = moment(p2.lastUpdate);
      return mp1.isAfter(mp2) ? 1 : -1;
    });

    // build an array of points
    const points = positions.map(p => {
      return { lat: p.latitude, lng: p.longitude };
    });

    if (points.length > 0) {
      const planeMapTrack = new google.maps.Polyline({
        path: points,
        geodesic: true,
        strokeColor: '#39ff14',
        strokeOpacity: 1.0,
        strokeWeight: 2,
        map: googleMap
      });

      this.googleMapFeatures.polylines.push(planeMapTrack);
    }
  }

  hidePlaneTrack(): void {
    for (let polyline of this.googleMapFeatures.polylines) {
      polyline.setMap(null);
      polyline.unbindAll();
    }

    this.googleMapFeatures.polylines = [];
  }

  setVisible(): void {
    this.isVisible = true;
    for (let marker of this.googleMapFeatures.markers) {
      marker.setVisible(true);
    }
  }

  setHidden(): void {
    this.isVisible = false;
    for (let marker of this.googleMapFeatures.markers) {
      marker.setVisible(false);
    }

    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  setSelected() {
    this.isSelected = true;

    this.setMarkerSelected();
    this.drawPlaneTrack(this.document, this.googleMap);
  }

  setUnselected() {
    this.isSelected = false;

    this.setMarkerNormal();
    this.hidePlaneTrack();
  }

  showLabel(): void {}

  hideLabel(): void {}

  destroy() {}

  buildMarkerImageUrls(layer: Layer, dataset: Dataset, document: Document, cacheBuster: number): MarkerUrls {
    let imageName = 'aircraft-large-alert';
    // if (!!document.aircraft_type && !!document.wtc) {
    //   // default to commercial aircraft marker: "aircraft-large"
    //   if (document.aircraft_type == 'Helicopter' || document.aircraft_type == 'Gyrocopter') {
    //     imageName = 'aircraft-helicopter';
    //   }
    //   if (document.aircraft_type == 'LandPlane' && document.wtc == 'L') {
    //     imageName = 'aircraft-small';
    //   }
    // }

    var rotation = '0';
    if (!!document.heading_direction) {
      // for the given track heading direction, round to the nearest reasonable heading rotation; the server will render any heading by interpreting this number in degrees
      var degree = parseInt(document.heading_direction);
      var roundingFactor = degree % 15;

      if (roundingFactor < 8) {
        // round down; 0 unchanged
        degree -= roundingFactor;
      } else {
        // round up
        degree += roundingFactor;
      }
      rotation = degree.toString();
    }

    const badgeCount = '0';
    const backColor = this.layerColors.fillColor.replace('#', '');
    const state = 'normal';

    return {
      normal: `/api/v1/meta/markers/${imageName}/${backColor}/${rotation}/${badgeCount}/${state}/${imageName}.png`,
      selected: `/api/v1/meta/markers/${imageName}/39ff14/${rotation}/${badgeCount}/${state}/${imageName}.png`
    };
  }

  getGeometryFeaturesForDisplay(document: Document): any[] {
    let fieldValue: any = null;
    try {
      fieldValue = JSON.parse(document.geometry);
    } catch {}

    if (!!!fieldValue) {
      return;
    }

    const features = GeoJsonUtilityService.getNormalizedGeometriesOrFeaturesFromGeoJson(fieldValue);
    if (!!!features) {
      return;
    }

    return features;
  }

  onMarkerMouseOver(e: FeatureEvent): void {
    if (this.mouseoutTimers[e.documentUId]) {
      clearTimeout(this.mouseoutTimers[e.documentUId]);
      this.mouseoutTimers[e.documentUId] = null;
    }

    this.setMarkerSelected();
    this.startHoverTimer();
  }

  onMarkerMouseOut(e: FeatureEvent): void {
    const that = this;
    const timeout = setTimeout(() => {
      if (!that.isSelected) {
        that.setMarkerNormal();
        clearTimeout(that.timerId);
        that.timerId = null;
      }
    }, 100);
    this.mouseoutTimers[e.documentUId] = timeout;
  }

  setMarkerSelected(): void {
    for (let marker of this.googleMapFeatures.markers) {
      const origZIndex = marker.getZIndex();
      const anchor = new google.maps.Point(16, 18);
      marker['__data'].zIndex = origZIndex;

      marker.setZIndex(SELECTED_Z_INDEX);
      marker.setIcon({
        url: this.markerUrls.selected,
        anchor
      });
    }
  }

  setMarkerNormal(): void {
    for (let marker of this.googleMapFeatures.markers) {
      const zIndex = marker['__data'].zIndex;
      const anchor = new google.maps.Point(16, 18);
      marker.setZIndex(zIndex);

      marker.setIcon({
        url: this.markerUrls.normal,
        anchor
      });
    }
  }

  startHoverTimer() {
    if (!!this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }

    const that = this;
    this.timerId = setTimeout(() => {
      if (!that.isSelected) {
        that.setMarkerNormal();
      }
    }, 3000);
  }
}
